import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    MagnifyingGlass,
    Sparkle,
    SealCheck,
    Crown,
    Star,
    ChalkboardTeacher,
    MapPin,
    Briefcase,
    Envelope,
    Phone,
    LinkedinLogo,
    Eye,
    ArrowRight,
    Lightning,
    FunnelSimple,
} from "@phosphor-icons/react";
import SavedSearchesPanel from "@/components/SavedSearchesPanel";
import CvActions from "@/components/CvActions";

const fmtExp = (n) => (n ? `${n} yr${n === 1 ? "" : "s"}` : "Fresher");
const fmtTime = (iso) => {
    if (!iso) return "";
    try {
        const dt = new Date(iso);
        const days = Math.floor((Date.now() - dt.getTime()) / 86400000);
        if (days < 1) return "today";
        if (days < 30) return `${days}d ago`;
        return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch { return ""; }
};

export default function Marketplace() {
    const [quota, setQuota] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null); // active candidate drawer

    // filters
    const [q, setQ] = useState("");
    const [locFilter, setLocFilter] = useState("");
    const [minExp, setMinExp] = useState("");
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [proOnly, setProOnly] = useState(false);
    const [featuredOnly, setFeaturedOnly] = useState(false);
    const [prepReadyOnly, setPrepReadyOnly] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 24 };
            if (q) params.q = q;
            if (locFilter) params.location = locFilter;
            if (minExp) params.min_experience = Number(minExp);
            if (verifiedOnly) params.verified_only = true;
            if (proOnly) params.pro_only = true;
            if (featuredOnly) params.featured_only = true;
            if (prepReadyOnly) params.prep_ready_only = true;
            const r = await api.get("/marketplace/candidates", { params });
            setRows(r.data.items || []);
            setTotal(r.data.total || 0);
            setQuota(r.data.quota);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load marketplace");
        } finally {
            setLoading(false);
        }
    }, [q, locFilter, minExp, verifiedOnly, proOnly, featuredOnly, prepReadyOnly]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // Analytics load — separate from search so filter changes don't reload it.
        api.get("/marketplace/analytics")
            .then((r) => setAnalytics(r.data))
            .catch(() => setAnalytics(null));
    }, []);

    const quotaBadge = useMemo(() => {
        if (!quota) return null;
        if (quota.limit === -1) return { label: "Unlimited views", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" };
        if (quota.remaining === 0) return { label: `0 of ${quota.limit} views left today · Upgrade for more`, tone: "text-red-700 bg-red-50 border-red-200" };
        if (quota.remaining <= 3) return { label: `${quota.remaining} of ${quota.limit} views left today`, tone: "text-amber-800 bg-amber-50 border-amber-200" };
        return { label: `${quota.remaining} of ${quota.limit} views left today`, tone: "text-[#525252] bg-[#F5F5F5] border-[#E5E5E5]" };
    }, [quota]);

    const openDrawer = async (c) => {
        setSelected({ ...c, loading: true });
        try {
            const r = await api.post(`/marketplace/candidates/${c.id}/view`);
            setQuota(r.data.quota);
            setSelected({ ...c, loading: false });
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error("Daily quota exhausted. Upgrade your plan for more views.");
                setSelected(null);
                return;
            }
            setSelected({ ...c, loading: false });
        }
    };

    const reveal = async (c) => {
        try {
            const r = await api.post(`/marketplace/candidates/${c.id}/reveal-contact`);
            setSelected({ ...c, ...r.data, revealed: true });
            setQuota(r.data.quota);
            toast.success("Contact revealed. Logged to audit trail.");
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error("Daily quota exhausted. Upgrade your plan for more views.");
            } else {
                toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Couldn't reveal contact");
            }
        }
    };

    return (
        <div className="max-w-7xl w-full min-w-0 mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="marketplace-page" style={{ maxWidth: "min(80rem, 100vw)" }}>
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-1 inline-flex items-center gap-1.5">
                        <Sparkle size={11} weight="fill" /> NaukriDaddy organic pool · cross-workspace
                    </p>
                    <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter">Talent Marketplace</h1>
                </div>
                {quotaBadge && (
                    <div data-testid="marketplace-quota"
                         className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border ${quotaBadge.tone}`}>
                        {quotaBadge.label}
                    </div>
                )}
            </div>
            <p className="text-sm text-[#525252] mb-6 max-w-2xl">
                Candidates who signed up on naukridaddy.com and want new opportunities.
                Click a card to view the full profile — reveals cost 1 view against your daily quota.
            </p>

            {/* Analytics strip — last-30d activity */}
            {analytics && (analytics.total_views > 0 || analytics.total_reveals > 0) && (
                <div data-testid="marketplace-analytics"
                     className="grid grid-cols-3 gap-3 mb-6 bg-white border border-[#E5E5E5] p-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">Views · 30d</p>
                        <p className="font-heading text-2xl font-black text-ink">{analytics.total_views}</p>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">Reveals · 30d</p>
                        <p className="font-heading text-2xl font-black text-cobalt">{analytics.total_reveals}</p>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">View → Contact</p>
                        <p className="font-heading text-2xl font-black text-emerald-600">{analytics.conversion_rate}%</p>
                    </div>
                </div>
            )}

            {/* Filter bar */}
            <SavedSearchesPanel
                currentFilters={{
                    q,
                    location: locFilter,
                    min_experience: minExp === "" ? null : Number(minExp),
                    verified_only: verifiedOnly,
                    pro_only: proOnly,
                    featured_only: featuredOnly,
                    prep_ready_only: prepReadyOnly,
                }}
                onApply={(f) => {
                    setQ(f.q || "");
                    setLocFilter(f.location || "");
                    setMinExp(f.min_experience != null ? String(f.min_experience) : "");
                    setVerifiedOnly(!!f.verified_only);
                    setProOnly(!!f.pro_only);
                    setFeaturedOnly(!!f.featured_only);
                    setPrepReadyOnly(!!f.prep_ready_only);
                }}
            />
            <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4 mb-6 space-y-3">
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="relative flex-1 min-w-[200px]">
                        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                        <input
                            data-testid="marketplace-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name, title or skill…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                        />
                    </div>
                    <input
                        data-testid="marketplace-location"
                        value={locFilter}
                        onChange={(e) => setLocFilter(e.target.value)}
                        placeholder="Location"
                        className="w-32 px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                    />
                    <input
                        data-testid="marketplace-min-exp"
                        type="number"
                        min="0"
                        value={minExp}
                        onChange={(e) => setMinExp(e.target.value)}
                        placeholder="Min exp (yrs)"
                        className="w-32 px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                    />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <FunnelSimple size={12} weight="bold" className="text-[#A3A3A3]" />
                    <FilterChip label="Verified only" icon={SealCheck} on={verifiedOnly} onToggle={() => setVerifiedOnly((v) => !v)} tone="emerald" testid="filter-verified" />
                    <FilterChip label="Pro Pass" icon={Crown} on={proOnly} onToggle={() => setProOnly((v) => !v)} tone="cobalt" testid="filter-pro" />
                    <FilterChip label="Featured" icon={Star} on={featuredOnly} onToggle={() => setFeaturedOnly((v) => !v)} tone="amber" testid="filter-featured" />
                    <FilterChip label="Interview-ready" icon={ChalkboardTeacher} on={prepReadyOnly} onToggle={() => setPrepReadyOnly((v) => !v)} tone="splash" testid="filter-prep-ready" />
                    <span className="ml-auto text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                        {loading ? "Loading…" : `${total} candidate${total === 1 ? "" : "s"}`}
                    </span>
                </div>
            </div>

            {/* Grid */}
            {loading ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {[0, 1, 2, 3, 4, 5].map((n) => (
                        <div key={n} className="h-56 border border-[#E5E5E5] bg-white animate-pulse" />
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <div className="border border-[#E5E5E5] bg-white p-10 text-center" data-testid="marketplace-empty">
                    <Sparkle size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                    <p className="font-heading text-lg font-black text-ink mb-1">No candidates match</p>
                    <p className="text-xs text-[#525252]">Try relaxing the filters or removing the search query.</p>
                </div>
            ) : (
                <div data-testid="marketplace-grid" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {rows.map((c, i) => (
                        <button key={c.id} onClick={() => openDrawer(c)}
                                data-testid={`marketplace-card-${i}`}
                                className="relative text-left bg-white border border-[#E5E5E5] hover:border-cobalt p-5 transition-all group">
                            {/* Premium badges */}
                            <div className="flex flex-wrap items-center gap-1 mb-2">
                                {c.featured && (
                                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5">
                                        <Star size={10} weight="fill" /> Featured
                                    </span>
                                )}
                                {c.verified && (
                                    c.verification_tier === "paid" ? (
                                        <span title="Paid Verified Badge (₹299) — ID docs approved by admin"
                                              className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-cobalt text-white px-1.5 py-0.5">
                                            <SealCheck size={10} weight="fill" /> Verified
                                        </span>
                                    ) : (
                                        <span title="KYC-verified (AI-verified Aadhaar / PAN). No paid badge."
                                              className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5">
                                            <SealCheck size={10} weight="fill" /> KYC-verified
                                        </span>
                                    )
                                )}
                                {c.pro && (
                                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-cobalt text-white px-1.5 py-0.5">
                                        <Crown size={10} weight="fill" /> Pro
                                    </span>
                                )}
                                {c.prep_ready && (
                                    <span title={c.prep_ready_state === "stale" ? "Prep sessions are >60 days old" : "Recent interview prep"}
                                          className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${
                                              c.prep_ready_state === "stale"
                                                  ? "bg-[#F5F5F5] text-[#A3A3A3]"
                                                  : "bg-splash text-ink"
                                          }`}>
                                        <ChalkboardTeacher size={10} weight="fill" />
                                        {c.prep_ready_state === "stale" ? "Prepped (stale)" : "Prepped"}
                                    </span>
                                )}
                                {!c.featured && !c.verified && !c.pro && !c.prep_ready && (
                                    <span className="text-[9px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                        Organic
                                    </span>
                                )}
                            </div>
                            <h3 className="font-heading text-lg font-bold tracking-tight text-ink truncate">
                                {c.name}
                            </h3>
                            {c.current_title && (
                                <p className="text-xs text-[#525252] flex items-center gap-1 mt-0.5 truncate">
                                    <Briefcase size={11} weight="fill" /> {c.current_title}
                                </p>
                            )}
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#525252]">
                                <span className="inline-flex items-center gap-1">
                                    <Lightning size={11} weight="fill" className="text-splash" /> {fmtExp(c.years_experience)}
                                </span>
                                {c.location && (
                                    <span className="inline-flex items-center gap-1 truncate">
                                        <MapPin size={11} weight="fill" /> {c.location}
                                    </span>
                                )}
                            </div>
                            {c.skills?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1">
                                    {c.skills.slice(0, 5).map((s) => (
                                        <span key={s} className="text-[10px] px-2 py-0.5 bg-[#F5F5F5] text-[#404040]">
                                            {s}
                                        </span>
                                    ))}
                                    {c.skills.length > 5 && (
                                        <span className="text-[10px] px-2 py-0.5 text-[#A3A3A3]">
                                            +{c.skills.length - 5}
                                        </span>
                                    )}
                                </div>
                            )}
                            <div className="mt-4 pt-3 border-t border-[#F5F5F5] flex items-center justify-between">
                                <span className="text-[10px] text-[#A3A3A3]">
                                    Updated {fmtTime(c.updated_at)}
                                </span>
                                <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-cobalt group-hover:text-ink">
                                    View <ArrowRight size={10} weight="bold" />
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Drawer */}
            {selected && (
                <CandidateDrawer c={selected} onClose={() => setSelected(null)} onReveal={reveal} />
            )}
        </div>
    );
}

// ---------- Filter chip ----------
function FilterChip({ label, icon: Icon, on, onToggle, tone, testid }) {
    const styles = on
        ? tone === "emerald" ? "bg-emerald-600 text-white border-emerald-600"
        : tone === "amber" ? "bg-amber-500 text-white border-amber-500"
        : tone === "splash" ? "bg-splash text-ink border-splash"
        : "bg-cobalt text-white border-cobalt"
        : "text-[#525252] border-[#E5E5E5] hover:border-ink";
    return (
        <button data-testid={testid} onClick={onToggle}
                className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 border transition-colors ${styles}`}>
            <Icon size={11} weight={on ? "fill" : "regular"} /> {label}
        </button>
    );
}

// ---------- Drawer with reveal-on-demand ----------
function CandidateDrawer({ c, onClose, onReveal }) {
    return (
        <div className="fixed inset-0 z-50 flex" data-testid="marketplace-drawer">
            <div className="flex-1 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
            <aside className="w-full max-w-md bg-white border-l border-[#E5E5E5] p-6 sm:p-8 overflow-y-auto">
                <button onClick={onClose} data-testid="marketplace-drawer-close"
                        className="text-[10px] font-black uppercase tracking-wider text-[#525252] hover:text-ink mb-4">
                    ← Close
                </button>
                <div className="flex flex-wrap items-center gap-1 mb-2">
                    {c.featured && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5">
                            <Star size={10} weight="fill" /> Featured
                        </span>
                    )}
                    {c.verified && (
                        c.verification_tier === "paid" ? (
                            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-cobalt text-white px-1.5 py-0.5">
                                <SealCheck size={10} weight="fill" /> Verified
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5">
                                <SealCheck size={10} weight="fill" /> KYC-verified
                            </span>
                        )
                    )}
                    {c.pro && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-cobalt text-white px-1.5 py-0.5">
                            <Crown size={10} weight="fill" /> Pro
                        </span>
                    )}
                    {c.prep_ready && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-splash text-ink px-1.5 py-0.5">
                            <ChalkboardTeacher size={10} weight="fill" /> Interview-ready
                        </span>
                    )}
                </div>
                <h2 className="font-heading text-3xl font-black tracking-tight text-ink">{c.name}</h2>
                {c.current_title && <p className="text-sm text-[#525252] mt-1">{c.current_title}</p>}
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#525252]">
                    <span>{fmtExp(c.years_experience)}</span>
                    {c.location && <span>· {c.location}</span>}
                </div>

                {/* V1.5.3 (a4) — CV actions on marketplace drawer (only after reveal) */}
                {c.revealed && c.id && (
                    <div className="mt-4">
                        <CvActions candidate={c} />
                    </div>
                )}

                {/* Contact block — masked until reveal */}
                <div className="mt-6 border border-[#E5E5E5] p-4 space-y-3">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">Contact</p>
                    <div className="flex items-center gap-2 text-sm">
                        <Envelope size={14} weight="fill" className="text-[#A3A3A3]" />
                        <span data-testid="marketplace-drawer-email"
                              className={c.revealed ? "text-ink" : "font-mono text-[#A3A3A3]"}>
                            {c.revealed ? (c.email || "—") : c.email_masked}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <Phone size={14} weight="fill" className="text-[#A3A3A3]" />
                        <span data-testid="marketplace-drawer-phone"
                              className={c.revealed ? "text-ink" : "font-mono text-[#A3A3A3]"}>
                            {c.revealed ? (c.phone || "—") : c.phone_masked}
                        </span>
                    </div>
                    {c.revealed && c.linkedin && (
                        <div className="flex items-center gap-2 text-sm">
                            <LinkedinLogo size={14} weight="fill" className="text-[#A3A3A3]" />
                            <a href={c.linkedin} target="_blank" rel="noreferrer"
                               className="text-cobalt hover:underline break-all">
                                {c.linkedin}
                            </a>
                        </div>
                    )}
                    {!c.revealed && (
                        <button onClick={() => onReveal(c)}
                                data-testid="marketplace-reveal-btn"
                                className="btn-gradient w-full py-3 text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 mt-2">
                            <Eye size={12} weight="bold" /> Reveal contact · 1 view
                        </button>
                    )}
                </div>

                {c.skills?.length > 0 && (
                    <div className="mt-6">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-2">Skills</p>
                        <div className="flex flex-wrap gap-1.5">
                            {c.skills.map((s) => (
                                <span key={s} className="text-xs px-2 py-1 bg-[#F5F5F5] text-[#262626]">{s}</span>
                            ))}
                        </div>
                    </div>
                )}

                <p className="mt-6 text-[10px] text-[#A3A3A3] italic border-t border-[#F5F5F5] pt-4">
                    Every reveal is logged in the DPDPA audit trail. Candidate is notified only if they enable notifications.
                </p>
            </aside>
        </div>
    );
}
